<template>
    <div class="select-modal-container animation-open-modal">
        <div>
            <ModalHeader
                :translation-base="translationBase"
                :title="title"
                :is-custom-fields="isCustomFields"
                :list="list"
                :isFilter="isFilter && title !== 'AppointmentType'"
                :close="closeModal"
                :stopLength="stopLength"
                :resourceLength="resourceLength"
                @changeTab="handleChangeTab"
            />

            <div v-if="showSearch" class="search-container">
                <div class="search-wrapper">
                    <span class="search-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="#998f8a"/>
                        </svg>
                    </span>
                    <input
                        ref="searchInput"
                        v-model="searchQuery"
                        class="search-input"
                        :placeholder="searchPlaceholder"
                    />
                    <button v-if="searchQuery" class="clear-button" @click="clearSearch">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#998f8a"/>
                        </svg>
                    </button>
                </div>
            </div>

            <ul :class="['list-content', isFilter ? '--has-filters' : '', showSearch ? '--has-search' : '']">
                <ItemList
                    v-for="(item, index) in filteredList"
                    :key="`item-${index}`"
                    :selected-id="multiple ? selectedIds : selectedId"
                    :item-id="item.id"
                    @select-item="onSelectItem(item)"
                >
                    {{ item.name }}
                </ItemList>
            </ul>
        </div>

        <div
            v-if="multiple"
            class="footer-modal-select"
        >
            <Button
                button-type="save"
                custom-class="footer-select-button"
                @click="onSave"
            >
                {{ $t("IntegrationPage.Save") }}
            </Button>
        </div>
    </div>
</template>

<script>
    import { mapMutations } from "vuex";
    import Button from "@/views/pages/operator/components/InputPhotoModal/components/Button/Button.vue";
    import ModalHeader from "./components/ModalHeader/ModalHeader.vue";
    import ItemList from "./components/ItemList/ItemList.vue";

    export default {
        components: {
            ModalHeader,
            ItemList,
            Button
        },
        props: {
            title: {
                type: String,
                default: "Reason",
                required: true
            },
            isCustomFields: {
                type: Boolean,
                default: false
            },
            isFilter: {
                type: Boolean,
                default: false
            },
            selectedId: {
                type: String | [],
                default: null
            },
            list: {
                type: Array,
                default: [],
                required: true
            },
            translationBase: {
                type: String,
                default: ""
            },
            multiple: {
                type: Boolean,
                default: false
            },
            stopLength: {
                type: Number,
                default: 0
            },
            resourceLength: {
                type: Number,
                default: 0
            }
        },
        data() {
            return {
                selectedIds:
                    this.multiple && Array.isArray(this.selectedId) ? [...this.selectedId] : [],
                selectedItemsWithType: [],
                searchQuery: ""
            };
        },
        computed: {
            showSearch() {
                const allowedTitles = [
                    "Resource",
                    "Material",
                    "Order",
                    "Operator.Filter",
                    "Filter",
                    "Reason",
                    "Status"
                ];
                return allowedTitles.includes(this.title) || this.list.length > 5;
            },
            filteredList() {
                if (!this.showSearch || !this.searchQuery) {
                    return this.list;
                }
                const query = this.normalizeText(this.searchQuery);
                return this.list.filter((item) => {
                    const name = this.normalizeText(item.name);
                    return name.includes(query);
                });
            },
            searchPlaceholder() {
                const keyWithBase = `${this.translationBase}SearchPlaceholder`;
                const rootKey = "SearchPlaceholder";
                
                const translatedWithBase = this.$t(keyWithBase);
                if (translatedWithBase !== keyWithBase) {
                    return translatedWithBase;
                }
                
                const translatedRoot = this.$t(rootKey);
                if (translatedRoot !== rootKey) {
                    return translatedRoot;
                }
                
                const nestedKey = "ProductionProgress.SelectModal.SearchPlaceholder";
                const translatedNested = this.$t(nestedKey);
                if (translatedNested !== nestedKey) {
                    return translatedNested;
                }
                
                const selectModalNestedKey = "SelectModal.SearchPlaceholder";
                const translatedSelectModalNested = this.$t(selectModalNestedKey);
                if (translatedSelectModalNested !== selectModalNestedKey) {
                    return translatedSelectModalNested;
                }
                
                return "Filtrar...";
            }
        },
        mounted() {
            this.UPDATE_FLAG_SHOW_OVERLAY(false);
            this.addOpenAnimation();
        },
        methods: {
            ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
            onSelectItem(item) {
                if (this.multiple) {
                    const index = this.selectedIds.indexOf(item.id);
                    if (index === -1) {
                        this.selectedIds.push(item.id);
                        this.isFilter && this.selectedItemsWithType.push(item);
                    } else {
                        this.selectedIds.splice(index, 1);
                    }
                } else {
                    this.$emit("select-item", { title: this.title, item });
                    this.closeModal();
                }
            },
            addOpenAnimation() {
                const modal = document.querySelector(".select-modal-container");
                modal.classList.add("animation-open-modal");

                setTimeout(() => {
                    modal.classList.remove("animation-open-modal");
                }, 500);
            },
            handleChangeTab(value) {
                this.searchQuery = "";
                this.$emit("changeTab", value);
            },
            normalizeText(text) {
                if (!text) return "";
                return String(text)
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .toLowerCase();
            },
            clearSearch() {
                this.searchQuery = "";
                this.$nextTick(() => {
                    this.$refs.searchInput?.focus();
                });
            },
            onSave() {
                this.$emit("select-item", {
                    title: this.title,
                    items: this.selectedIds,
                    itemsWithType: this.selectedItemsWithType
                });
                this.closeModal();
            },
            closeModal() {
                const modal = document.querySelector(".select-modal-container");
                modal.classList.add("animation-close-modal");
                modal.classList.add("closed-modal");

                setTimeout(() => this.$emit("close", this.title), 500);
            }
        }
    };
</script>

<style>
    body:has(.select-modal-container) {
        overflow: hidden;
    }
</style>

<style lang="scss" scoped>
    .select-modal-container {
        height: 100vh;
        width: 100vw;
        background: #fcfcfc;
        position: fixed;
        top: 0;
        left: 0;
        z-index: 9999999999;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        &.closed-modal {
            top: 100vh;
        }

        &.animation-open-modal {
            animation: openModal 0.5s normal;
            @keyframes openModal {
                0% {
                    top: 100vh;
                }
                100% {
                    top: 0;
                }
            }
        }

        &.animation-close-modal {
            animation: closeModal 0.5s normal;
            @keyframes closeModal {
                0% {
                    top: 0;
                }
                100% {
                    top: 100vh;
                }
            }
        }

        .search-container {
            padding: 12px 29px;
            background-color: #fcfcfc;
            border-bottom: 1px solid #cfc4be;

            .search-wrapper {
                display: flex;
                align-items: center;
                background: #ffffff;
                border: 1.5px solid #cfc4be;
                border-radius: 8px;
                padding: 8px 14px;
                gap: 10px;
                transition: border-color 0.3s, box-shadow 0.3s;

                &:focus-within {
                    border-color: #a35205;
                    box-shadow: 0 0 0 3px rgba(163, 82, 5, 0.15);
                }

                .search-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .search-input {
                    flex-grow: 1;
                    border: none;
                    outline: none;
                    background: transparent;
                    font-size: 16px;
                    color: #4c4541;
                    font-family: inherit;

                    &::placeholder {
                        color: #998f8a;
                        opacity: 0.8;
                    }
                }

                .clear-button {
                    border: none;
                    background: transparent;
                    padding: 0;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: opacity 0.2s;

                    &:hover {
                        opacity: 0.7;
                    }
                }
            }
        }

        .list-content {
            height: calc(100vh - 143px);
            overflow: auto;
            padding: 0;
            margin: 0;

            &.--has-filters {
                height: calc(100vh - 199px);
            }

            &.--has-search {
                height: calc(100vh - 211px);

                &.--has-filters {
                    height: calc(100vh - 267px);
                }
            }

            &::-webkit-scrollbar {
                width: 16px; /* width of the entire scrollbar */
            }

            &::-webkit-scrollbar-track {
                background: #fff;
            }

            &::-webkit-scrollbar-thumb {
                background-color: #cfc4be; /* color of the scroll thumb */
                border-radius: 20px; /* roundness of the scroll thumb */
                border: 4px solid #fff;
                height: 70px;
            }

            @media (max-width: 768px) and (min-width: 593px) {
                height: calc(100vh - 180px);
                &.--has-search {
                    height: calc(100vh - 248px);
                }
            }
            @media (max-width: 593px) {
                height: calc(100vh - 155px);
                &.--has-search {
                    height: calc(100vh - 223px);
                }
            }
            @media (max-width: 425px) {
                height: calc(100vh - 131px);
                &.--has-search {
                    height: calc(100vh - 199px);
                }
            }
        }

        .footer-modal-select {
            display: flex;
            justify-content: flex-end;
            padding: 15.5px 29px;
            border-top: 1px solid #cfc4be;
            background: #fff;
            box-shadow:
                0px 3px 4px 0px rgba(0, 0, 0, 0.14),
                0px 3px 3px 0px rgba(0, 0, 0, 0.12),
                0px 1px 8px 0px rgba(0, 0, 0, 0.2);
        }
    }
</style>
